import { computed, isVNode, defineComponent } from 'vue';
import Tabs from '../tabs';
import PropTypes from '../_util/vue-types';
import { flattenChildren, isEmptyElement, filterEmptyWithUndefined } from '../_util/props-util';
import isPlainObject from 'lodash-es/isPlainObject';
import useConfigInject from '../config-provider/hooks/useConfigInject';
import { useConfigContextInject } from '../config-provider/context';
import devWarning from '../vc-util/devWarning';
import useStyle from './style';
import Skeleton from '../skeleton';
import { objectType, stringType } from '../_util/type';
import { customRenderSlot } from '../_util/vnode';
const { TabPane } = Tabs;
export const cardProps = () => ({
    prefixCls: String,
    title: PropTypes.any,
    extra: PropTypes.any,
    /** @deprecated Please use `variant` instead. */
    bordered: { type: Boolean, default: true },
    /** Card style variant (antd ≥ 5.24). */
    variant: stringType(),
    /** @deprecated Please use `styles.body` instead. */
    bodyStyle: { type: Object, default: undefined },
    /** @deprecated Please use `styles.header` instead. */
    headStyle: { type: Object, default: undefined },
    loading: { type: Boolean, default: false },
    hoverable: { type: Boolean, default: false },
    type: { type: String },
    size: { type: String },
    actions: PropTypes.any,
    tabList: {
        type: Array,
    },
    tabBarExtraContent: PropTypes.any,
    activeTabKey: String,
    defaultActiveTabKey: String,
    cover: PropTypes.any,
    onTabChange: {
        type: Function,
    },
    /** Semantic structure className (antd ≥ 5.23). */
    classNames: objectType(),
    /** Semantic structure style (antd ≥ 5.23). */
    styles: objectType(),
});
const Card = defineComponent({
    compatConfig: { MODE: 3 },
    name: 'ACard',
    inheritAttrs: false,
    props: cardProps(),
    slots: Object,
    setup(props, { slots, attrs }) {
        const { prefixCls, direction, size } = useConfigInject('card', props);
        const { card: ctxCard } = useConfigContextInject();
        const [wrapSSR, hashId] = useStyle(prefixCls);
        const mergedVariant = computed(() => {
            if (props.variant) {
                return props.variant;
            }
            if (props.bordered === false) {
                return 'borderless';
            }
            return 'outlined';
        });
        const getAction = (actions) => {
            const actionList = actions.map((action, index) => (isVNode(action) && !isEmptyElement(action)) || !isVNode(action) ? (<li style={{ width: `${100 / actions.length}%` }} key={`action-${index}`}>
            <span>{action}</span>
          </li>) : null);
            return actionList;
        };
        const triggerTabChange = (key) => {
            var _a;
            (_a = props.onTabChange) === null || _a === void 0 ? void 0 : _a.call(props, key);
        };
        const isContainGrid = (obj = []) => {
            let containGrid;
            obj.forEach(element => {
                if (element && isPlainObject(element.type) && element.type.__ANT_CARD_GRID) {
                    containGrid = true;
                }
            });
            return containGrid;
        };
        return () => {
            var _a, _b, _c, _d, _e, _f;
            const { headStyle = {}, bodyStyle = {}, loading, type, tabList, hoverable, activeTabKey, defaultActiveTabKey, tabBarExtraContent = filterEmptyWithUndefined((_a = slots.tabBarExtraContent) === null || _a === void 0 ? void 0 : _a.call(slots)), title = filterEmptyWithUndefined((_b = slots.title) === null || _b === void 0 ? void 0 : _b.call(slots)), extra = filterEmptyWithUndefined((_c = slots.extra) === null || _c === void 0 ? void 0 : _c.call(slots)), actions = filterEmptyWithUndefined((_d = slots.actions) === null || _d === void 0 ? void 0 : _d.call(slots)), cover = filterEmptyWithUndefined((_e = slots.cover) === null || _e === void 0 ? void 0 : _e.call(slots)), classNames: cardClassNames, styles: cardStyles, } = props;
            const cardCfg = ctxCard === null || ctxCard === void 0 ? void 0 : ctxCard.value;
            const children = flattenChildren((_f = slots.default) === null || _f === void 0 ? void 0 : _f.call(slots));
            const pre = prefixCls.value;
            const mergedHeadStyle = Object.assign(Object.assign({}, headStyle), cardStyles === null || cardStyles === void 0 ? void 0 : cardStyles.header);
            const mergedBodyStyle = Object.assign(Object.assign({}, bodyStyle), cardStyles === null || cardStyles === void 0 ? void 0 : cardStyles.body);
            const classString = {
                [`${pre}`]: true,
                [hashId.value]: true,
                [`${pre}-loading`]: loading,
                [`${pre}-bordered`]: mergedVariant.value !== 'borderless',
                [`${pre}-hoverable`]: !!hoverable,
                [`${pre}-contain-grid`]: isContainGrid(children),
                [`${pre}-contain-tabs`]: tabList && tabList.length,
                [`${pre}-${size.value}`]: size.value,
                [`${pre}-type-${type}`]: !!type,
                [`${pre}-rtl`]: direction.value === 'rtl',
            };
            const loadingBlock = (<Skeleton loading active paragraph={{ rows: 4 }} title={false}>
          {children}
        </Skeleton>);
            const hasActiveTabKey = activeTabKey !== undefined;
            const tabsProps = {
                size: 'large',
                [hasActiveTabKey ? 'activeKey' : 'defaultActiveKey']: hasActiveTabKey
                    ? activeTabKey
                    : defaultActiveTabKey,
                onChange: triggerTabChange,
                class: `${pre}-head-tabs`,
            };
            let head;
            const tabs = tabList && tabList.length ? (<Tabs {...tabsProps} v-slots={{ rightExtra: tabBarExtraContent ? () => tabBarExtraContent : null }}>
            {tabList.map(item => {
                    const { tab: temp, slots: itemSlots } = item;
                    const name = itemSlots === null || itemSlots === void 0 ? void 0 : itemSlots.tab;
                    devWarning(!itemSlots, 'Card', `tabList slots is deprecated, Please use \`customTab\` instead.`);
                    let tab = temp !== undefined ? temp : slots[name] ? slots[name](item) : null;
                    tab = customRenderSlot(slots, 'customTab', item, () => [tab]);
                    return <TabPane tab={tab} key={item.key} disabled={item.disabled}/>;
                })}
          </Tabs>) : null;
            if (title || extra || tabs) {
                head = (<div class={[`${pre}-head`, cardClassNames === null || cardClassNames === void 0 ? void 0 : cardClassNames.header]} style={mergedHeadStyle}>
            <div class={`${pre}-head-wrapper`}>
              {title && (<div class={[`${pre}-head-title`, cardClassNames === null || cardClassNames === void 0 ? void 0 : cardClassNames.title]} style={cardStyles === null || cardStyles === void 0 ? void 0 : cardStyles.title}>
                  {title}
                </div>)}
              {extra && (<div class={[`${pre}-extra`, cardClassNames === null || cardClassNames === void 0 ? void 0 : cardClassNames.extra]} style={cardStyles === null || cardStyles === void 0 ? void 0 : cardStyles.extra}>
                  {extra}
                </div>)}
            </div>
            {tabs}
          </div>);
            }
            const coverDom = cover ? (<div class={[`${pre}-cover`, cardClassNames === null || cardClassNames === void 0 ? void 0 : cardClassNames.cover]} style={cardStyles === null || cardStyles === void 0 ? void 0 : cardStyles.cover}>
          {cover}
        </div>) : null;
            const body = (<div class={[`${pre}-body`, cardClassNames === null || cardClassNames === void 0 ? void 0 : cardClassNames.body]} style={mergedBodyStyle}>
          {loading ? loadingBlock : children}
        </div>);
            const actionDom = actions && actions.length ? (<ul class={[`${pre}-actions`, cardClassNames === null || cardClassNames === void 0 ? void 0 : cardClassNames.actions]} style={cardStyles === null || cardStyles === void 0 ? void 0 : cardStyles.actions}>
            {getAction(actions)}
          </ul>) : null;
            return wrapSSR(<div ref="cardContainerRef" {...attrs} class={[classString, cardCfg === null || cardCfg === void 0 ? void 0 : cardCfg.className, cardClassNames === null || cardClassNames === void 0 ? void 0 : cardClassNames.root, attrs.class]} style={Object.assign(Object.assign(Object.assign({}, cardCfg === null || cardCfg === void 0 ? void 0 : cardCfg.style), cardStyles === null || cardStyles === void 0 ? void 0 : cardStyles.root), attrs.style)}>
          {head}
          {coverDom}
          {children && children.length ? body : null}
          {actionDom}
        </div>);
        };
    },
});
export default Card;
